import { RefObject, useCallback } from 'react'

import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'
import { reportAnalyticsFrameRoute } from 'shared/services/analytics/navigation/analyticsFrames'
import { IframeWindowContent, WindowState } from 'shared/types/index'

import { useWindowUrl } from '../components/WindowControls/useWindowUrl'
import { useContainerBridge } from './useContainerBridge'
import { resolveCurrentIframeSrc } from './useHistoryManagement.utils'
import { useInjectIframeScript } from './useInjectIframeScript'

export const useHistoryManagement = (
  iframeRef: RefObject<HTMLIFrameElement>,
  {
    iframeId,
    window
  }: {
    iframeId: string
    window: WindowState
  }
) => {
  const { windows, setCurrentNavigationIndex, addToWindowHistory } =
    useWindowStore()

  const targetWindow = windows.find(w => w.id === window.id)

  const { currentHistoryIndex = 0, history = [] } =
    targetWindow ?? ({} as IframeWindowContent)

  const { historyAvailability, isContainerBridgeAvailable, messages } =
    useContainerBridge(iframeRef, {
      onNavigation: path => handleNavigate({ path })
    })

  const currentWindowURL = useWindowUrl(window.id)
  const currentWindowPath =
    new URL(currentWindowURL).pathname + new URL(currentWindowURL).search
  const hasPersistedHistory = history?.length > 0

  const hasBack = isContainerBridgeAvailable
    ? historyAvailability.hasBack
    : currentHistoryIndex > 0

  const hasForward = isContainerBridgeAvailable
    ? historyAvailability.hasForward
    : currentHistoryIndex < (history?.length || 0) - 1

  const currentUrl = resolveCurrentIframeSrc({
    fallbackWindowUrl: window.url ?? '',
    currentWindowPath,
    isContainerBridgeAvailable
  })

  useInjectIframeScript(
    iframeRef,
    `
      const __CONTAINER_NAV_HAS_PERSISTED_HISTORY__ = ${hasPersistedHistory}

      /* notify parent about any navigation */
      function sendRouteChange () {
        window.__CONTAINER_NAV_INITIALIZED__ = true
        if("${currentWindowPath}" === location.pathname + location.search) {
          return
        }

        parent.postMessage(
          {
            type: 'IFRAME_NAV',
            iframeId: '${iframeId}',
            route: location.pathname + location.search
          },
          '*'
        )
      }

      /* keep originals */
      const originalPush = history.pushState.bind(history)
      const originalReplace = history.replaceState.bind(history)

      /* wrap pushState */
      history.pushState = function (state, title, url) {
        const target =
          url == null
            ? location.pathname + location.search
            : url instanceof URL ? url.toString() : url
        originalPush(state, title, target)
        sendRouteChange()
      }

      /* wrap replaceState */
      history.replaceState = function (state, title, url) {
        const target =
          url == null
            ? location.pathname + location.search
            : url instanceof URL ? url.toString() : url
        originalReplace(state, title, target)
        sendRouteChange()
      }

      window.addEventListener('popstate', sendRouteChange)

      /* listen for parent‑driven navigations */
      window.addEventListener('message', (event) => {
        if (event.data?.type !== 'PARENT_NAV_TO_IFRAME') return
        const { route } = event.data
        const current = location.pathname + location.search
        if (route && current !== route) {
          originalPush(null, '', route)
        }
      })

      window.addEventListener('unload', () => {
        parent.postMessage(
          {
            type: 'IFRAME_UNLOAD',
            iframeId: '${iframeId}',
          },
          '*'
        )
      })

      function handleInit() {
        if(window.__CONTAINER_NAV_INITIALIZED__) {return;}
        window.__CONTAINER_NAV_INITIALIZED__ = true

        sendRouteChange(); 
      }
    `
  )

  const handleBack = useCallback(() => {
    if (!iframeRef.current) return

    if (isContainerBridgeAvailable) {
      messages.postBackMessage(window.id)
      return
    }

    if (currentHistoryIndex <= 0) return

    if (!window.url) return

    setCurrentNavigationIndex(iframeId, prev => {
      const res = Math.max(prev - 1, 0)
      const nextSrc = history?.at(res) ?? window.url ?? ''
      iframeRef.current!.src = nextSrc
      return res
    })
  }, [
    iframeRef,
    isContainerBridgeAvailable,
    currentHistoryIndex,
    window.url,
    window.id,
    setCurrentNavigationIndex,
    iframeId,
    messages,
    history
  ])

  function handleNavigate({ path }: { path: string }) {
    if (iframeRef.current) reportAnalyticsFrameRoute(iframeRef.current, path)
    addToWindowHistory(window.id, path)
    setCurrentNavigationIndex(window.id, window.history?.length ?? 0)
  }

  const handleForward = useCallback(() => {
    if (!iframeRef.current) return

    if (isContainerBridgeAvailable) {
      messages.postForwardMessage(window.id)
      return
    }

    if (!window.url) return

    setCurrentNavigationIndex(iframeId, prev => {
      const res = Math.max(prev + 1, 0)
      const nextSrc = history.at(res) ?? window.url ?? ''
      iframeRef.current!.src = nextSrc
      return res
    })
  }, [
    iframeRef,
    isContainerBridgeAvailable,
    window.url,
    window.id,
    setCurrentNavigationIndex,
    iframeId,
    messages,
    history
  ])

  return {
    hasBack,
    hasForward,
    handleBack,
    handleNavigate,
    handleForward,
    currentUrl
  }
}
